import { memo } from 'react';
import { Crown, Gift } from 'lucide-react';
import type { StaffHonour, StaffHonourKind } from '../../utils/staffTickets';
import { entriesLabel, formatTicketMonth } from '../../utils/staffTickets';
import { cn } from '../../utils/cn';

const TONE: Record<StaffHonourKind, string> = {
  mvp: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  raffle: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
};

const ICON: Record<StaffHonourKind, typeof Crown> = { mvp: Crown, raffle: Gift };

export const HONOUR_LABEL: Record<StaffHonourKind, string> = { mvp: 'MVP', raffle: 'Raffle winner' };

const describeHonour = (h: StaffHonour): string =>
  `${HONOUR_LABEL[h.kind]} · ${formatTicketMonth(h.month, 'long')} · ${h.kind === 'mvp' ? `${h.tickets} tickets closed` : `drawn with ${entriesLabel(h.entries ?? 0)}`}`;

interface StaffHonourBadgeProps {
  honour: StaffHonour;
  /** Icon + month only (tables, rails). */
  compact?: boolean;
  className?: string;
}

/** Pill for a monthly honour: MVP (most tickets closed) or raffle winner (drawn from the runners-up). */
export const StaffHonourBadge = memo(({ honour, compact = false, className }: StaffHonourBadgeProps) => {
  const Icon = ICON[honour.kind];
  return (
    <span
      className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap', TONE[honour.kind], className)}
      title={describeHonour(honour)}
    >
      <Icon className="w-3 h-3" aria-hidden="true" />
      {compact ? formatTicketMonth(honour.month, 'name') : `${HONOUR_LABEL[honour.kind]} · ${formatTicketMonth(honour.month)}`}
    </span>
  );
});

StaffHonourBadge.displayName = 'StaffHonourBadge';

interface StaffHonourListProps {
  honours: StaffHonour[];
  /** Pills shown before the rest collapse into `+N`. */
  max?: number;
  compact?: boolean;
  className?: string;
}

/** Honour pills, newest first, with overflow collapsed into a `+N` count. Renders nothing when empty. */
export const StaffHonourList = ({ honours, max = 3, compact = false, className }: StaffHonourListProps) => {
  if (honours.length === 0) return null;
  const shown = honours.slice(0, max);
  const rest = honours.slice(max);
  return (
    <span className={cn('inline-flex flex-wrap items-center gap-1', className)}>
      {shown.map((h) => (
        <StaffHonourBadge key={`${h.kind}-${h.month}`} honour={h} compact={compact} />
      ))}
      {rest.length > 0 && (
        <span className="text-[11px] font-mono text-glass-500 tabular-nums" title={rest.map(describeHonour).join('\n')}>
          +{rest.length}
        </span>
      )}
    </span>
  );
};
